'use client';

import { useCallback, useMemo, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';

import { Button } from '@/components/ui/Button';
import { InlineErrorAlert } from '@/components/ui/InlineErrorAlert';
import { InlineInfoAlert } from '@/components/ui/InlineInfoAlert';
import { useCheckout, useIsCheckoutBusy } from '@/hooks/useCheckout';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useRouter } from '@/lib/i18n/navigation';
import { useOptionalCartUI } from '@/providers/cartProvider';
import { registerWatch } from '@/lib/myWatches/client';
import type { Watch } from '@/lib/myWatches/types';
import { WARRANTY_EXTENSION_VARIANT_ID } from '@/lib/const';
import { EVENT_NAME } from '@/lib/gtm/const';
import type { WatchProductSummary } from '@/lib/service/ecs/contracts';
import {
  canRegisterWatchFromSidebar,
  getRepairDetailsSidebarEligibility,
  type WarrantyExtensionOffer,
} from '@/lib/service/repairDetailsSidebarEligibility';
import { buildRepairRequestWatchPath } from '@/lib/service/repairRequestQuery';
import { repairRequestActions } from '@/lib/service/repairRequestState';
import { checkoutActions } from '@/states/checkout';
import { formatPrice } from '@/lib/utils/formatPrice';

import styles from './RepairDetailsWatchSidebar.module.scss';

interface RepairDetailsWatchSidebarAccountActionsProps {
  warrantyCode: number | null;
  warranty: Watch | null;
  productSummary: WatchProductSummary | null;
  referenceNumber: string;
  serialNumber: string;
  ecsAvailable: boolean;
  warrantyExtensionOffer: WarrantyExtensionOffer | null;
  onWatchRegistered: () => Promise<void>;
}

export function RepairDetailsWatchSidebarAccountActions({
  warrantyCode,
  warranty,
  productSummary,
  referenceNumber,
  serialNumber,
  ecsAvailable,
  warrantyExtensionOffer,
  onWatchRegistered,
}: Readonly<RepairDetailsWatchSidebarAccountActionsProps>) {
  const locale = useLocale();
  const router = useRouter();
  const t = useTranslations('service.repairDetails.sidebar');
  const { addLine } = useCheckout();
  const isCheckoutBusy = useIsCheckoutBusy();
  const openDrawer = useOptionalCartUI()?.openDrawer;
  const { sendIntentionEvent } = useGoogleAnalytics();
  const [isProcessing, setIsProcessing] = useState(false);
  const [extendError, setExtendError] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerSuccess, setRegisterSuccess] = useState(false);

  const eligibility = useMemo(
    () =>
      getRepairDetailsSidebarEligibility({
        warrantyCode,
        warranty,
        productSummary,
        warrantyExtensionOffer,
        ecsAvailable,
      }),
    [ecsAvailable, productSummary, warranty, warrantyCode, warrantyExtensionOffer]
  );

  const formattedExtensionPrice = warrantyExtensionOffer
    ? formatPrice(
        warrantyExtensionOffer.price.amount,
        warrantyExtensionOffer.price.currency,
        locale
      )
    : null;

  const handleExtendWarranty = useCallback(async () => {
    if (!warrantyExtensionOffer || !WARRANTY_EXTENSION_VARIANT_ID) {
      return;
    }

    sendIntentionEvent(EVENT_NAME.INTENTION_WARRANTY_EXTENSION);
    setExtendError(null);
    setIsProcessing(true);
    try {
      const result = await addLine({
        variantId: WARRANTY_EXTENSION_VARIANT_ID,
        name: t('warrantyExtensionProduct', { duration: warrantyExtensionOffer.duration }),
        quantity: 1,
        productType: 'warranty',
        cta: 'ORDER',
        price: warrantyExtensionOffer.price.amount,
        currency: warrantyExtensionOffer.price.currency,
        warrantyExtensionDuration: warrantyExtensionOffer.duration,
        metadata: [
          { key: 'warranty.duration', value: warrantyExtensionOffer.duration },
          { key: 'related.watch.model_reference', value: referenceNumber },
          { key: 'related.watch.serial_number', value: serialNumber },
        ],
      });

      if (result.success) {
        openDrawer?.();
        return;
      }

      const message = result.errors[0]?.message ?? t('extendWarrantyError');
      setExtendError(message);
      checkoutActions.addError(message);
      openDrawer?.();
    } catch {
      const message = t('extendWarrantyError');
      setExtendError(message);
      checkoutActions.addError(message);
      openDrawer?.();
    } finally {
      setIsProcessing(false);
    }
  }, [
    addLine,
    openDrawer,
    referenceNumber,
    sendIntentionEvent,
    serialNumber,
    t,
    warrantyExtensionOffer,
  ]);

  const handleNeedService = useCallback(() => {
    repairRequestActions.clear();
    router.push(buildRepairRequestWatchPath(serialNumber));
  }, [router, serialNumber]);

  const handleRegisterWatch = useCallback(async () => {
    setRegisterError(null);
    setRegisterSuccess(false);

    if (!canRegisterWatchFromSidebar(eligibility)) {
      setRegisterError(t('registerIneligible'));
      return;
    }

    if (!serialNumber || !productSummary?.sku) {
      setRegisterError(t('registerIneligible'));
      return;
    }

    setIsProcessing(true);
    try {
      const result = await registerWatch({
        sn: serialNumber,
        sku: productSummary.sku,
        locale,
      });

      if (result.status === 'success') {
        setRegisterSuccess(true);
        await onWatchRegistered();
        return;
      }

      setRegisterError(t('registerIneligible'));
    } finally {
      setIsProcessing(false);
    }
  }, [eligibility, locale, onWatchRegistered, productSummary?.sku, serialNumber, t]);

  if (
    !eligibility.showExtendWarranty &&
    !eligibility.showNeedService &&
    !eligibility.showRegisterWatch
  ) {
    return null;
  }

  return (
    <div className={styles.actions} data-testid="RepairDetailsWatchSidebar-account-actions">
      {registerSuccess ? (
        <InlineInfoAlert id="repair-details-register-success">
          {t('registerSuccess')}
        </InlineInfoAlert>
      ) : null}

      {registerError ? (
        <InlineErrorAlert
          id="repair-details-register-error"
          data-testid="RepairDetailsWatchSidebar-register-error"
        >
          {registerError}
        </InlineErrorAlert>
      ) : null}

      {extendError ? (
        <InlineErrorAlert
          id="repair-details-extend-error"
          data-testid="RepairDetailsWatchSidebar-extend-error"
        >
          {extendError}
        </InlineErrorAlert>
      ) : null}

      {eligibility.showExtendWarranty && warrantyExtensionOffer ? (
        <div className={styles.actionBlock}>
          <div className={styles.actionSummary}>
            <p className={styles.actionSummaryText}>
              {t('extendWarrantyYears', { years: warrantyExtensionOffer.duration })}
            </p>
            {formattedExtensionPrice ? (
              <p className={styles.actionSummaryPrice}>{formattedExtensionPrice}</p>
            ) : null}
          </div>
          <Button
            type="button"
            variant="outline"
            fullWidth
            loading={isProcessing}
            disabled={isProcessing || isCheckoutBusy || !WARRANTY_EXTENSION_VARIANT_ID}
            onClick={() => void handleExtendWarranty()}
            data-testid="RepairDetailsWatchSidebar-extend-warranty"
          >
            {t('extendMyWarranty')}
          </Button>
        </div>
      ) : null}

      {eligibility.showNeedService ? (
        <Button
          type="button"
          variant="outline"
          fullWidth
          onClick={handleNeedService}
          data-testid="RepairDetailsWatchSidebar-need-service"
        >
          {t('needsService')}
        </Button>
      ) : null}

      {eligibility.showRegisterWatch ? (
        <div className={styles.actionBlock}>
          <p className={styles.actionSummaryText}>{t('registerEnjoyMore')}</p>
          <Button
            type="button"
            fullWidth
            loading={isProcessing}
            disabled={isProcessing}
            onClick={() => void handleRegisterWatch()}
            data-testid="RepairDetailsWatchSidebar-register-watch"
          >
            {t('registerMyWatch')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
